import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../components/Button/Button";
import CustomDialog from "../components/dialog/CustomDialog";
import ReusableTable from "../components/Table/ReusableTable";
import { get, post } from "../utils/api";
import NotFound from "./NotFound";
import { InputText } from "primereact/inputtext";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { SquarePen, Search, Filter, Download, Plus } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Check } from "lucide-react";

const UserMasterPage = () => {
  const navigate = useNavigate();
  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogMode, setDialogMode] = useState("add");
  const [currentUserData, setCurrentUserData] = useState(null);
  const [userData, setUserData] = useState([]);
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [nextUserId, setNextUserId] = useState("");

  const userId = localStorage.getItem("user_details")
    ? JSON.parse(localStorage.getItem("user_details")).id
    : null;

  useEffect(() => {
    if (!userId) {
      navigate("/not", { replace: true });
    } else {
      fetchUserData();
      fetchRoles();
    }
  }, [userId, navigate]);

  const fetchUserData = async () => {
    try {
      const { data } = await get("/user/fetchUsers");
      setUserData(data?.Data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch user data");
    }
  };

  const fetchRoles = async () => {
    try {
      const { data } = await get("/role/fetchActiveRoles");
      setRoles(data?.Data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch roles");
    }
  };

  const fetchNextUserId = async () => {
    try {
      const { data } = await get("/user/fetchNextUserId");
      const fetchedNextUserId = data?.Data?.nextUserId;
      const resolvedNextUserId =
        fetchedNextUserId !== undefined && fetchedNextUserId !== null
          ? String(fetchedNextUserId)
          : "";

      setNextUserId(resolvedNextUserId);
      return resolvedNextUserId;
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch next user ID");
      setNextUserId("");
      return "";
    }
  };

  const downloadCSV = () => {
    if (!userData || userData.length === 0) {
      toast.warning("No data to download.");
      return;
    }

    const headers = [
      "User ID",
      "User Name",
      "Email ID",
      "Mobile Number",
      "Role",
      "PAN Number",
      "Status",
    ];

    const rows = userData.map((user) => {
      const role =
        roles.find((r) => r.id === user.role_id)?.role_code || user.role_id;
      const status = user.is_active ? "Active" : "Inactive";
      return [
        user.id,
        user.name,
        user.email,
        user.mobile_number,
        role,
        user.pan_number, // Add this field
        status
      ];
    });

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers, ...rows]
        .map((e) => e.map((v) => `"${v ?? ""}"`).join(","))
        .join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "user_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredData = useMemo(() => {
    if (!searchTerm) return userData;
    const lower = searchTerm.toLowerCase();
    return userData.filter(user =>
      (user.name && user.name.toLowerCase().includes(lower)) ||
      (user.email && user.email.toLowerCase().includes(lower)) ||
      (user.mobile_number && String(user.mobile_number).toLowerCase().includes(lower)) ||
      (user.id && String(user.id).toLowerCase().includes(lower))
    );
  }, [userData, searchTerm]);

  const salesUsers = useMemo(() => {
    return (userData || []).filter((user) => {
      const foundRole = roles.find((r) => String(r.id) === String(user.role_id));
      const roleCode = (foundRole?.role_code || user.role_code || "").toString().toUpperCase();
      return roleCode.includes("SALES");
    });
  }, [userData, roles]);

  const columns = useMemo(
    () => [
      {
        Header: "Action",
        Cell: ({ row }) => (
          <Button
            variant="ghost"
            className="text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 p-2 rounded-lg transition-all"
            onClick={() => {
              const u = row.original;
              setCurrentUserData({
                id: u.id,
                userName: u.name,
                email: u.email,
                mobileNumber: u.mobile_number,
                roleId: u.role_id,
                panNumber: u.pan_number,
                isActive: u.is_active,
              });
              setDialogMode("edit");
              setDialogVisible(true);
            }}
          >
            <SquarePen size={18} />
          </Button>
        ),
        disableSortBy: true,
        disableFilters: true,
        size: 80,
      },
      {
        Header: "User ID",
        accessor: "id",
        size: 120,
        Cell: ({ value }) => <span className="font-bold text-gray-900">{value}</span>
      },
      {
        Header: "User Name",
        accessor: "name",
        size: 200,
        Cell: ({ value }) => (
          <div className="font-bold text-gray-800 uppercase leading-tight max-w-[180px] whitespace-normal line-clamp-2 break-words mx-auto" title={value}>
            {value}
          </div>
        )
      },
      { Header: "Email ID", accessor: "email", size: 300 },
      { Header: "Mobile Number", accessor: "mobile_number", size: 180 },
      {
        Header: "Role",
        accessor: (row) => {
          const found = roles.find((r) => r.id === row.role_id);
          return found ? found.role_code : row.role_id;
        },
        Cell: ({ value }) => {
          let styles = "bg-gray-100 text-gray-600";
          const role = String(value).toUpperCase();

          if (role.includes("ADMIN")) styles = "bg-red-50 text-red-600 border border-red-100";
          else if (role.includes("DISTRIBUTOR")) styles = "bg-indigo-50 text-indigo-600 border border-indigo-100";
          else if (role.includes("SALES")) styles = "bg-cyan-50 text-cyan-600 border border-cyan-100";
          else if (role.includes("OPERATIONS")) styles = "bg-orange-50 text-orange-600 border border-orange-100";

          return (
            <span className={`px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${styles}`}>
              {value}
            </span>
          );
        },
        size: 150,
      },
      {
        Header: "PAN Number",
        accessor: "pan_number", // match the field returned by your API
        size: 150,
      },
      {
        Header: "Status",
        accessor: "is_active",
        Cell: ({ value }) => {
          const statusText = value ? "Active" : "Inactive";
          const bgColor = value ? "bg-[#EBFFF3]" : "bg-[#F3F4F6]";
          const textColor = value ? "text-[#10B981]" : "text-[#6B7280]";
          const dotColor = value ? "bg-[#10B981]" : "bg-[#9CA3AF]";

          return (
            <div
              className={`flex items-center gap-2 px-3 py-1 rounded-full border ${bgColor} ${textColor} text-[11px] font-bold w-fit mx-auto`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
              {statusText}
            </div>
          );
        },
        size: 150,
      },
    ],
    [roles]
  );

  if (!userId) return <NotFound />;

  // --------------------- USER DIALOG ----------------------
  const UserDialog = ({ visible, onHide, mode, initialData, onSubmit }) => {
    const [formData, setFormData] = useState({
      id: "",
      userId: "",
      userName: "",
      email: "",
      mobileNumber: "",
      roleId: "",
      panNumber: "",
      isActive: true,
      salesUser: "",
    });

    const [errors, setErrors] = useState({});

    const getInitialFormData = () => ({
      id: initialData?.id || "",
      userId:
        mode === "add" ? nextUserId || "" : initialData?.id || "",
      userName: initialData?.userName || "",
      email: initialData?.email || "",
      mobileNumber: initialData?.mobileNumber || "",
      roleId: initialData?.roleId || "",
      panNumber: initialData?.panNumber || "",
      isActive: initialData?.isActive ?? true,
      salesUser: "",
    });

    useEffect(() => {
      if (!visible) return;
      setFormData(getInitialFormData());
      setErrors({});
    }, [initialData, visible, mode, nextUserId]);

    const selectedRole = roles.find(
      (role) => String(role.id) === String(formData.roleId)
    );
    const isDistributorRole =
      String(selectedRole?.role_code || "")
        .trim()
        .toUpperCase() === "DISTRIBUTOR";

    const handleChange = (field) => (e) => {
      let value = field === "isActive" ? e.target.checked : e.target.value;

      // Character restrictions
      if (field === "mobileNumber" && !/^\d*$/.test(value)) return;
      if (field === "userName") {
        if (!/^[a-zA-Z\s]*$/.test(value)) return;
        value = value.toUpperCase();
      }

      setFormData((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    };

    const validate = () => {
      const errs = {};

      if (mode === "add" && !formData.userId)
        errs.userId = "User ID is required.";

      if (!formData.userName) errs.userName = "User Name is required.";

      if (!formData.email) {
        errs.email = "Email is required.";
      } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
        errs.email = "Invalid email format.";
      }

      if (!formData.mobileNumber) {
        errs.mobileNumber = "Mobile Number is required.";
      } else if (!/^\d{10}$/.test(formData.mobileNumber)) {
        errs.mobileNumber = "Mobile Number must be 10 digits.";
      }

      if (!formData.roleId) errs.roleId = "Role is required.";

      // ✅ Required and valid format for PAN Number
      const validFourthChars = ["P", "C", "H", "T", "F", "B", "J", "L", "E", "G"];
      const fourthChar = (formData.panNumber || "").trim().charAt(3).toUpperCase();

      if (!formData.panNumber) {
        errs.panNumber = "PAN Number is required.";
      } else if (
        !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber.toUpperCase())
      ) {
        errs.panNumber = "Invalid PAN Number format.";
      } else if (!validFourthChars.includes(fourthChar)) {
        errs.panNumber = "The 4th character of PAN Number must be one of: P, C, H, T, F, B, J, L, E, G.";
      }

      setErrors(errs);
      return Object.keys(errs).length === 0;
    };

    const handleSubmit = () => {
      if (!validate()) return;
      onSubmit(formData);
    };

    const renderError = (field) => (
      errors[field] ? (
        <div className="pt-1">
          <p className="text-red-500 text-[10px] font-bold leading-4">{errors[field]}</p>
        </div>
      ) : null
    );

    const inputClassName = (field) =>
      `w-full px-4 py-2 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 bg-white h-[42px] transition-shadow ${errors[field]
        ? "border border-red-300 focus:ring-red-100 focus:border-red-400"
        : "border border-gray-200 focus:ring-indigo-500"
      }`;

    return (
      <CustomDialog
        visible={visible}
        onHide={onHide}
        header={
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-gray-900">{mode === "add" ? "Add User" : "Edit User"}</span>
          </div>
        }
        width="640px"
      >
        <div className="space-y-6 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
            {mode === "add" && (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">User ID <span className="text-red-500">*</span></label>
                <InputText
                  value={formData.userId}
                  className={`${inputClassName("userId")} bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed`}
                  placeholder="Auto-generated User ID"
                  readOnly
                />
                {renderError("userId")}
              </div>
            )}

            {mode === "edit" && (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">User ID <span className="text-red-500">*</span></label>
                <div className="w-full h-[42px] px-4 rounded-lg bg-gray-50 border border-gray-200 flex items-center text-sm font-bold text-gray-400">
                  {formData.id || "-"}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">User Name <span className="text-red-500">*</span></label>
              <InputText
                value={formData.userName}
                onChange={handleChange("userName")}
                className={`${inputClassName("userName")} uppercase`}
                placeholder="Enter Full Name"
              />
              {renderError("userName")}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Email <span className="text-red-500">*</span></label>
              <InputText
                type="email"
                value={formData.email}
                onChange={handleChange("email")}
                className={inputClassName("email")}
                placeholder="example@amc.com"
              />
              {renderError("email")}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <InputText
                maxLength={10}
                value={formData.mobileNumber}
                onChange={handleChange("mobileNumber")}
                className={inputClassName("mobileNumber")}
                placeholder="10 digit mobile"
              />
              {renderError("mobileNumber")}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Role <span className="text-red-500">*</span></label>
              <select
                value={formData.roleId}
                onChange={handleChange("roleId")}
                className={`w-full px-4 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 bg-white h-[42px] transition-shadow appearance-none cursor-pointer ${errors.roleId
                  ? "border border-red-300 focus:ring-red-100 focus:border-red-400"
                  : "border border-gray-200 focus:ring-indigo-500"
                  } ${!formData.roleId ? "text-gray-400" : "text-gray-800"
                  }`}
                style={{
                  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 1rem center',
                  backgroundSize: '1em'
                }}
              >
                <option value="" disabled className="text-gray-400">Select Role</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id} className="text-gray-800">
                    {r.role_code}
                  </option>
                ))}
              </select>
              {renderError("roleId")}
            </div>

            {isDistributorRole && (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
                  Sales RM
                </label>
                <select
                  value={formData.salesUser}
                  onChange={handleChange("salesUser")}
                  className={`w-full px-4 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 bg-white h-[42px] transition-shadow appearance-none cursor-pointer border border-gray-200 focus:ring-indigo-500 ${!formData.salesUser ? "text-gray-400" : "text-gray-800"
                    }`}
                  style={{
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 1rem center',
                    backgroundSize: '1em'
                  }}
                >
                  <option value="" className="text-gray-400">Select Sales User</option>
                  {salesUsers.map((su) => (
                    <option key={su.id} value={su.name || su.username || su.id} className="text-gray-800">
                      {su.name || su.username}
                    </option>
                  ))}
                </select>
                {renderError("salesUser")}
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">PAN Number <span className="text-red-500">*</span></label>
              <InputText
                value={formData.panNumber}
                onChange={handleChange("panNumber")}
                className={`${inputClassName("panNumber")} uppercase`}
                maxLength={10}
                placeholder="ABCDE1234F"
              />
              {renderError("panNumber")}
            </div>

            {mode === "edit" && (
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Status</label>
                <div className="flex items-center gap-3 bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                  <div
                    className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors ${formData.isActive ? "bg-indigo-500" : "bg-gray-200"
                      }`}
                    onClick={() => handleChange("isActive")({ target: { checked: !formData.isActive } })}
                  >
                    <div
                      className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-transform ${formData.isActive ? "translate-x-6" : "translate-x-1"
                        }`}
                    />
                  </div>
                  <span className={`text-sm font-bold ${formData.isActive ? "text-indigo-600" : "text-gray-500"}`}>
                    {formData.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-5">
          <button
            onClick={onHide}
            className="px-6 py-2 text-sm font-bold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-8 py-2 bg-indigo-600 text-white rounded-lg font-bold text-sm hover:bg-indigo-700 shadow-lg shadow-indigo-200 active:scale-95 transition-all flex items-center gap-2"
          >
            <Check className="w-4 h-4" />
            {mode === "add" ? "Submit User" : "Update User"}
          </button>
        </div>
      </CustomDialog>
    );
  };

  // ------------------ HANDLE SUBMIT ------------------
  const handleDialogSubmit = async (data) => {
    try {
      const payload = {
        username: data.userName,
        name: data.userName,
        email: data.email,
        mobile_number: data.mobileNumber,
        role_id: data.roleId,
        pan_number: data.panNumber,
        is_active: dialogMode === "add" ? true : data.isActive,
        user_Id: userId,
        id: data.id,
      };

      const api =
        dialogMode === "add" ? "/user/createUser" : "/user/updateUser";
      const { data: resp } = await post(api, payload);

      if (resp.Success) {
        toast.success(
          dialogMode === "add"
            ? "User Created Successfully"
            : "User Updated Successfully"
        );
        setDialogVisible(false);
        setCurrentUserData(null);
        fetchUserData();
      } else {
        // 🔔 Handle already existing user case
        if (
          resp.Message &&
          resp.Message.toLowerCase().includes("already") // you can also check for "exists"
        ) {
          toast.error("User already exists!");
        } else {
          toast.error(resp.Message || "Operation failed");
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while submitting user data.");
    }
  };

  return (
    <div className="w-full max-w-screen-xl mx-auto p-4 h-full min-h-screen bg-gray-50/30">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-800 tracking-tight">User Management</h1>
        <p className="text-[11px] text-gray-400 font-medium tracking-wide">Manage system users and their permissions</p>
      </div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3 w-full max-w-2xl">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by user, email, or mobile..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow bg-white h-[42px]"
            />
          </div>
          <button
            type="button"
            onClick={downloadCSV}
            className="flex items-center gap-2 text-gray-700 border border-gray-200 hover:bg-gray-50 bg-white h-[42px] px-4 rounded-lg font-medium shadow-sm transition-colors"
          >
            <Download className="w-4 h-4 text-gray-500" /><span className="text-sm">Export To Excel</span>
          </button>
        </div>

        <button
          className="flex items-center gap-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white shadow-sm border-none h-[42px] px-5 rounded-lg transition-colors"
          onClick={async () => {
            await fetchNextUserId();
            setDialogMode("add");
            setCurrentUserData(null);
            setDialogVisible(true);
          }}
        >
          <Plus className="w-4 h-4" />
          <span className="font-medium text-sm">Add User</span>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <ReusableTable columns={columns} data={filteredData} />
      </div>

      <UserDialog
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        mode={dialogMode}
        initialData={currentUserData}
        onSubmit={handleDialogSubmit}
      />

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default UserMasterPage;
